import React from "react";
import { useState } from "react";
import { motion } from "framer-motion";
import { Formik, useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";

import Logo from "../../components/Logo";
import TailwindInput from "../../components/Tailwind/TailwindInput";
import TailwindButton from "../../components/Tailwind/TailwindButton";
import { createOrganization, ApiError } from "../../shared/api/auth";
import { cn } from "../../lib/utils";

let easing = [0.6, -0.05, 0.01, 0.99];
const fadeInUp = {
  initial: {
    y: 40,
    opacity: 0,
    transition: { duration: 0.6, ease: easing },
  },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: easing,
    },
  },
};

const CreateOrganization = ({ create_succeeds }) => {
  const [switchChecked, setSwitchChecked] = useState(false);
  const [organizationError, setOrganizationError] = useState({
    message: '',
    variant: ''
  });

  const handleClickSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSwitchChecked(true);
      setValues({
        ...values,
        billing_address: values.address,
        billing_city: values.city,
        billing_post_code: values.post_code,
        billing_country: values.country
      });
    } else {
      setSwitchChecked(false);
      setValues({
        ...values,
        billing_address: '',
        billing_city: '',
        billing_post_code: '',
        billing_country: '',
      });
    }
  }

  let easing2 = [0.6, -0.05, 0.01, 0.99];
  const animate = {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: easing2,
      delay: 0.16,
    },
  };

  const OrganizationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Organization name required"),
    address: Yup.string()
      .required("Address required"),
    city: Yup.string()
      .required("City required"),
    post_code: Yup.string()
      .required("Post code required"),
    country: Yup.string()
      .required("Country required"),
    billing_address: Yup.string()
      .required("Billing address required"),
    billing_city: Yup.string()
      .required("Billing city required"),
    billing_post_code: Yup.string()
      .required("Billing post code required"),
    billing_country: Yup.string()
      .required("Country required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      city: "",
      post_code: "",
      country: "",
      billing_address: "",
      billing_city: "",
      billing_post_code: "",
      billing_country: "",
    },

    validationSchema: OrganizationSchema,
    onSubmit: () => {
      createOrganization({
        name: values.name,
        address: values.address,
        city: values.city,
        post_code: values.post_code,
        country: values.country,
        billing_address: values.billing_address,
        billing_city: values.billing_city,
        billing_post_code: values.billing_post_code,
        billing_country: values.billing_country,
      }).then(() => {
        setSubmitting(false);
        create_succeeds();
      }).catch((error) => {
        setSubmitting(false);
        if (error instanceof ApiError && error.status === 404) {
          create_succeeds();
        } else {
          const detail = error instanceof ApiError && error.body
            ? (error.body as { detail?: string }).detail
            : undefined;
          setOrganizationError({
            message: detail || 'An error occurred',
            variant: 'error'
          });
        }
      });
    },
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps, setFieldValue, setValues, setSubmitting } = formik;

  return (
    <div className="grid place-items-center">
      <div className="w-full max-w-[640px]">
        <div className="max-w-[480px] p-6 mx-auto flex justify-center flex-col">
          <motion.div {...fadeInUp} className="text-center">
            <Logo />
            <div>
              <div>
                <div className="flex justify-center items-center">
                  <h2 className="font-bold text-xl mb-4 text-center">
                    Fill in the informations about your organization
                  </h2>
                </div>
                <div className="mb-12">
                  <FormikProvider value={formik}>
                    <Form
                      autoComplete="off"
                      noValidate
                      onSubmit={handleSubmit}>
                      <div className="space-y-4">
                        <motion.div
                          className="space-y-6"
                          initial={{ opacity: 0, y: 40 }}
                          animate={animate}
                        >
                          <TailwindInput
                            fullWidth
                            autoComplete="username"
                            label="Name"
                            {...getFieldProps("name")}
                            error={touched.name && errors.name ? errors.name : undefined}
                            onChange={(e) => {
                              setFieldValue("name", e.target.value);
                              setOrganizationError({
                                message: '',
                                variant: ''
                              });
                            }}
                          />
                        </motion.div>

                        <div className="flex justify-end items-center gap-2">
                          <span className="text-[10px] text-slate-600">
                            Billing address at the same address
                          </span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={switchChecked}
                              onChange={handleClickSwitch}
                            />
                            <div className={cn(
                              "w-11 h-6 bg-slate-200 rounded-full peer",
                              "peer-focus:ring-4 peer-focus:ring-blue-300",
                              "peer-checked:after:translate-x-full peer-checked:after:border-white",
                              "after:content-[''] after:absolute after:top-0.5 after:left-[2px]",
                              "after:bg-white after:border-slate-300 after:border after:rounded-full",
                              "after:h-5 after:w-5 after:transition-all",
                              "peer-checked:bg-blue-600"
                            )}></div>
                          </label>
                        </div>

                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                          initial={{ opacity: 0, y: 60 }}
                          animate={animate}
                        >
                          <TailwindInput
                            fullWidth
                            label="Address"
                            {...getFieldProps("address")}
                            error={touched.address && errors.address ? errors.address : undefined}
                            onChange={(e) => {
                              setFieldValue("address", e.target.value);
                              if (switchChecked)
                                setFieldValue("billing_address", e.target.value);
                            }}
                          />

                          <TailwindInput
                            fullWidth
                            label="Billing address"
                            {...getFieldProps("billing_address")}
                            error={touched.billing_address && errors.billing_address ? errors.billing_address : undefined}
                            readOnly={switchChecked ? true : false}
                          />
                        </motion.div>

                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                          initial={{ opacity: 0, y: 60 }}
                          animate={animate}
                        >
                          <TailwindInput
                            fullWidth
                            label="City"
                            {...getFieldProps("city")}
                            error={touched.city && errors.city ? errors.city : undefined}
                            onChange={(e) => {
                              setFieldValue("city", e.target.value);
                              if (switchChecked)
                                setFieldValue("billing_city", e.target.value);
                            }}
                          />

                          <TailwindInput
                            fullWidth
                            label="Billing city"
                            {...getFieldProps("billing_city")}
                            error={touched.billing_city && errors.billing_city ? errors.billing_city : undefined}
                            readOnly={switchChecked ? true : false}
                          />
                        </motion.div>

                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                          initial={{ opacity: 0, y: 60 }}
                          animate={animate}
                        >
                          <TailwindInput
                            fullWidth
                            label="Post code"
                            {...getFieldProps("post_code")}
                            error={touched.post_code && errors.post_code ? errors.post_code : undefined}
                            onChange={(e) => {
                              setFieldValue("post_code", e.target.value);
                              if (switchChecked)
                                setFieldValue("billing_post_code", e.target.value);
                            }}
                          />

                          <TailwindInput
                            fullWidth
                            label="Billing post code"
                            {...getFieldProps("billing_post_code")}
                            error={touched.billing_post_code && errors.billing_post_code ? errors.billing_post_code : undefined}
                            readOnly={switchChecked ? true : false}
                          />
                        </motion.div>

                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                          initial={{ opacity: 0, y: 60 }}
                          animate={animate}
                        >
                          <TailwindInput
                            fullWidth
                            label="Country"
                            {...getFieldProps("country")}
                            error={touched.country && errors.country ? errors.country : undefined}
                            onChange={(e) => {
                              setFieldValue("country", e.target.value);
                              if (switchChecked)
                                setFieldValue("billing_country", e.target.value);
                            }}
                          />

                          <TailwindInput
                            fullWidth
                            label="Billing country"
                            {...getFieldProps("billing_country")}
                            error={touched.billing_country && errors.billing_country ? errors.billing_country : undefined}
                            readOnly={switchChecked ? true : false}
                          />
                        </motion.div>
                      </div>

                      {organizationError.message ? (
                        <div className={cn(
                          "mt-4 p-3 rounded-lg text-sm",
                          organizationError.variant === 'error' && "bg-red-50 text-red-800 border border-red-200"
                        )}>
                          {organizationError.message}
                        </div>
                      ) : null}

                      <div className="mt-5 flex justify-center">
                        <TailwindButton
                          fullWidth
                          size="lg"
                          type="submit"
                          disabled={isSubmitting}
                          className="bg-black hover:bg-black/90 mb-5"
                        >
                          {isSubmitting ? (
                            <>
                              <span className="material-symbols-outlined animate-spin text-[18px]">
                                progress_activity
                              </span>
                              Loading...
                            </>
                          ) : (
                            'Validate'
                          )}
                        </TailwindButton>
                      </div>
                    </Form>
                  </FormikProvider>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
